import {
  useId,
  useMergeRefs,
} from "@floating-ui/react"
import classNames from "classnames"
import type {
  ChangeEventHandler,
  ComponentPropsWithoutRef,
  Ref,
} from "react"
import {
  forwardRef,
  useCallback,
  useRef,
  useState,
  useLayoutEffect,
} from "react"
import styles from "./Slider.module.scss"
import { useSliderContext } from "./SliderContext"
import { UnstyledInput } from "src/inputs/UnstyledInputs"

export interface Props extends ComponentPropsWithoutRef<"input"> {
  min: number;
  max: number;
  value?: string | number;
  defaultValue?: string | number;
}

/*
 * this element renders a range input
 */
export const SliderInput = forwardRef<HTMLInputElement, Props>(
  function SliderInput(props: Props, propRef: Ref<HTMLInputElement>) {
    const internalRef = useRef<HTMLInputElement>(null)
    const uncontrolledId = useId()

    const ref = useMergeRefs([
      propRef,
      internalRef,
    ])

    const {
      onChange: controlledOnChange,
      value: controlledValue,
      disabled = false,
      id = uncontrolledId,
      defaultValue = "",
      ...htmlProps
    } = props

    const {
      inputValues,
      addInput,
      removeInput,
      setSliderDisabled,
    } = useSliderContext()

    const [
      uncontrolledValue,
      setUncontrolledValue,
    ] = useState<string | number>(defaultValue)

    // when the value changes, update the input values and uncontrolled value
    const onChange: ChangeEventHandler<HTMLInputElement> = useCallback(
      (e) => {
        addInput(
          id,
          Number(e.target.value),
        )

        setUncontrolledValue(e.target.value)
        controlledOnChange?.(e)
      },
      [
        id,
        addInput,
        setUncontrolledValue,
        controlledOnChange,
      ],
    )

    // when the controlledValue changes, update the input values
    useLayoutEffect(
      () => {
        if (controlledValue !== null && controlledValue !== undefined) {
          addInput(
            id,
            Number(controlledValue),
          )
        }
      },
      [
        addInput,
        controlledValue,
        id,
      ],
    )

    // when the component first mounts, update the input values
    useLayoutEffect(
      () => {
        const currentValue = internalRef.current?.value
        if (currentValue) {
          addInput(
            id,
            Number(currentValue),
          )
        }

        return () => removeInput(id)
      },
      [
        id,
        addInput,
        removeInput,
      ],
    )

    // tell the container that the select input has been disabled
    useLayoutEffect(
      () => {
        setSliderDisabled(disabled)
      },
      [
        disabled,
        setSliderDisabled,
      ],
    )

    const className = classNames(
      htmlProps.className,
      styles.sliderInput,
      {
        [styles.multiSliderInput]: inputValues.length > 1,
      },
    )

    return (
      <UnstyledInput
        {...htmlProps}
        className={className}
        disabled={disabled}
        onChange={onChange}
        ref={ref}
        type="range"
        value={controlledValue ?? uncontrolledValue}
      />
    )
  },
)
